const FALLBACK_CANONICAL_COLUMNS = {
  YEAR: "Year",
  MAKE: "Make",
  MODEL: "Model",
  PART_NAME: "PartName",
  PART_CATEGORY: "PartCategory",
  PRICE: "Price",
  QUANTITY: "Quantity",
  POSTAL_CODE: "PostalCode",
  DESCRIPTION: "DetailedDescription",
  STRIPE_LINK: "StripePaymentLink"
};

const FALLBACK_REQUIRED_COLUMNS = [
  "Year",
  "Make",
  "Model",
  "PartName",
  "PartCategory",
  "Price",
  "Quantity",
  "PostalCode"
];

const FALLBACK_COLUMN_VARIATIONS = {
  Year: [
    "year",
    "Year",
    "YEAR",
    "yr",
    "Yr",
    "model year",
    "ModelYear",
    "model_year",
    "vehicle year",
    "VehicleYear"
  ],
  Make: [
    "make",
    "Make",
    "MAKE",
    "manufacturer",
    "Manufacturer",
    "brand",
    "Brand",
    "car make",
    "CarMake",
    "car_make"
  ],
  Model: [
    "model",
    "Model",
    "MODEL",
    "car model",
    "CarModel",
    "car_model",
    "vehicle model",
    "VehicleModel"
  ],
  PartName: [
    "part name",
    "partname",
    "PartName",
    "PART NAME",
    "part_name",
    "PARTNAME",
    "name",
    "Name",
    "part",
    "Part",
    "product name",
    "ProductName",
    "product_name",
    "item name",
    "ItemName"
  ],
  PartCategory: [
    "part category",
    "partcategory",
    "PartCategory",
    "PART CATEGORY",
    "part_category",
    "PARTCATEGORY",
    "category",
    "Category",
    "type",
    "Type",
    "part type",
    "PartType",
    "part_type"
  ],
  Price: [
    "price",
    "Price",
    "PRICE",
    "cost",
    "Cost",
    "amount",
    "Amount",
    "unit price",
    "UnitPrice",
    "unit_price",
    "sale price",
    "SalePrice",
    "sale_price",
    "$",
    "price ($)"
  ],
  Quantity: [
    "quantity",
    "Quantity",
    "QUANTITY",
    "qty",
    "Qty",
    "QTY",
    "stock",
    "Stock",
    "amount",
    "Amount",
    "count",
    "Count",
    "available",
    "Available",
    "in stock",
    "InStock",
    "in_stock"
  ],
  PostalCode: [
    "postal code",
    "postalcode",
    "PostalCode",
    "POSTAL CODE",
    "postal_code",
    "POSTALCODE",
    "zip",
    "Zip",
    "ZIP",
    "zip code",
    "ZipCode",
    "zip_code",
    "postcode",
    "PostCode",
    "post_code"
  ],
  DetailedDescription: [
    "detailed description",
    "detaileddescription",
    "DetailedDescription",
    "DETAILED DESCRIPTION",
    "detailed_description",
    "description",
    "Description",
    "DESCRIPTION",
    "desc",
    "Desc",
    "details",
    "Details",
    "notes",
    "Notes",
    "comments",
    "Comments"
  ],
  StripePaymentLink: [
    "stripe payment link",
    "stripepaymentlink",
    "StripePaymentLink",
    "STRIPE PAYMENT LINK",
    "stripe_payment_link",
    "payment link",
    "PaymentLink",
    "payment_link",
    "stripe link",
    "StripeLink",
    "stripe_link",
    "checkout link",
    "CheckoutLink",
    "checkout_link",
    "payment url",
    "PaymentURL",
    "payment_url",
    "Stripe payment"
  ]
};

// Use imported values or fallbacks
let REQUIRED_COLUMNS_TO_USE;
let COLUMN_VARIATIONS_TO_USE;

try {
  // Try to import from Columns
  const { REQUIRED_COLUMNS, COLUMN_VARIATIONS } = require("./Columns");
  REQUIRED_COLUMNS_TO_USE = REQUIRED_COLUMNS || FALLBACK_REQUIRED_COLUMNS;
  COLUMN_VARIATIONS_TO_USE = COLUMN_VARIATIONS || FALLBACK_COLUMN_VARIATIONS;
} catch (error) {
  REQUIRED_COLUMNS_TO_USE = FALLBACK_REQUIRED_COLUMNS;
  COLUMN_VARIATIONS_TO_USE = FALLBACK_COLUMN_VARIATIONS;
}

export const transformRowWithMapping = (row, columnMapping) => {
  const transformed = {};

  Object.entries(row).forEach(([originalCol, value]) => {
    const canonicalCol = columnMapping[originalCol];
    if (canonicalCol) {
      transformed[canonicalCol] = value;
    } else {
      transformed[originalCol] = value;
    }
  });

  return transformed;
};

const normalizeColumnName = (name) => {
  if (!name || typeof name !== "string") return "";
  return name
    .toLowerCase()
    .trim()
    .replace(/[_\-\s]+/g, " ")
    .replace(/[^a-z0-9\s]/g, "");
};

export const mapColumnsToCanonical = (headers) => {
  const mapping = {};
  const unmappedColumns = [];
  const missingRequiredColumns = [];

  if (!headers || !Array.isArray(headers)) {
    console.error("Invalid headers provided:", headers);
    return {
      mapping,
      unmappedColumns,
      missingRequiredColumns: REQUIRED_COLUMNS_TO_USE
    };
  }

  const normalizedHeaders = headers.map((h) => ({
    original: h,
    normalized: normalizeColumnName(h)
  }));

  const foundCanonicalColumns = new Set();

  normalizedHeaders.forEach((header) => {
    let matched = false;
    for (const [canonical, variations] of Object.entries(
      COLUMN_VARIATIONS_TO_USE
    )) {
      if (header.original === canonical) {
        mapping[header.original] = canonical;
        foundCanonicalColumns.add(canonical);
        matched = true;
        break;
      }

      const normalizedVariations = variations.map((v) =>
        normalizeColumnName(v)
      );
      if (normalizedVariations.includes(header.normalized)) {
        mapping[header.original] = canonical;
        foundCanonicalColumns.add(canonical);
        matched = true;
        break;
      }
    }

    if (!matched) {
      unmappedColumns.push(header.original);
    }
  });

  if (REQUIRED_COLUMNS_TO_USE && Array.isArray(REQUIRED_COLUMNS_TO_USE)) {
    REQUIRED_COLUMNS_TO_USE.forEach((requiredColumn) => {
      if (!foundCanonicalColumns.has(requiredColumn)) {
        missingRequiredColumns.push(requiredColumn);
      }
    });
  } else {
    console.error(
      "REQUIRED_COLUMNS_TO_USE is invalid:",
      REQUIRED_COLUMNS_TO_USE
    );
  }

  return { mapping, unmappedColumns, missingRequiredColumns };
};
